import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";
import { downloadFile, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const ENTITY = { order: "Order", dispatch: "Dispatch", receipt: "GRN", customer: "Customer", part: "Part", design: "Design", asset_group: "Asset group", asset_unit: "Asset unit" };
const LINK = { order: (id) => `/orders/${id}`, customer: (id) => `/masters/customers?open=${id}`, part: (id) => `/masters/parts?open=${id}`, design: (id) => `/designs/${id}`, asset_group: (id) => `/assets/groups/${id}`, asset_unit: (id) => `/assets/units/${id}` };

export default function Documents() {
  const [q, setQ] = useState("");
  const [et, setEt] = useState("all");
  const [dt, setDt] = useState("all");
  const { data: types } = useFetch("/documents/types");
  const { data } = useFetch(`/documents/library?q=${encodeURIComponent(q)}${et !== "all" ? `&entity_type=${et}` : ""}${dt !== "all" ? `&doc_type=${encodeURIComponent(dt)}` : ""}`);
  return (
    <div data-testid="documents-page">
      <PageHeader title="Documents" subtitle="Every file attached to customers, parts, orders, dispatches, GRNs and designs. Upload from the record itself." />
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input placeholder="Search file name, title, record no…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm bg-white" data-testid="documents-search-input" />
        <Select value={et} onValueChange={setEt}>
          <SelectTrigger className="sm:w-44 bg-white" data-testid="documents-entity-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All records</SelectItem>{Object.entries(ENTITY).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={dt} onValueChange={setDt}>
          <SelectTrigger className="sm:w-44 bg-white" data-testid="documents-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{(types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="th text-left"><th className="px-3 py-2.5">File</th><th className="px-3 py-2.5">Record</th><th className="px-3 py-2.5">Type</th><th className="px-3 py-2.5">Original name</th><th className="px-3 py-2.5">Uploaded</th><th /></tr></thead>
          <tbody>
            {(data || []).map((d) => (
              <tr key={d.id} className="border-b border-slate-100" data-testid={`library-row-${d.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs">{d.stored_name}</td>
                <td className="px-3 py-2.5 text-xs"><span className="text-slate-400 uppercase font-mono">{ENTITY[d.entity_type]}</span> {LINK[d.entity_type] ? <Link className="text-sky-700 font-mono" to={LINK[d.entity_type](d.entity_id)}>{d.entity_label}</Link> : <span className="font-mono">{d.entity_label}</span>}</td>
                <td className="px-3 py-2.5 text-xs">{d.doc_type} · v{d.version}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500 break-all">{d.original_filename}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{d.uploaded_by?.name} · {fmtDateTime(d.uploaded_at)}</td>
                <td className="px-3 py-2.5"><Button variant="ghost" size="icon" onClick={() => downloadFile(`/documents/${d.id}/download`, d.stored_name).catch((e) => toast.error(errMsg(e)))} data-testid={`library-download-${d.id}`}><Download className="h-4 w-4" /></Button></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">No documents found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
